// TcTemplatesPage — the office's transaction-coordinator checklist templates, one per state
// (IL / FL) x our side (buyer / seller / dual) x deal type (residential / commercial / lease).
// The managing broker or office manager adds, removes and reorders steps, sets each step's
// default holder (role), and when it is due: N business / calendar days before or after the
// contract date, the closing date or one of the deal's milestones (lib/dealMilestones keys).
// A saved template replaces the built-in default for that combination; "Reset" brings the
// default back. Also: the office person who holds "Office staff / TC" steps by default.
// Deals already running keep their own checklist. API: /api/tc/templates, /api/tc/settings
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser } from '../../hooks/useCurrentUser';

const BASIS = { business: 'business days', calendar: 'calendar days', calendar_roll: 'calendar days (rolls past weekend/holiday)' };
const inp = 'p-2 border rounded';
const BLANK = { skey: '', label: '', role: 'tc', days: 0, basis: 'business', anchor: 'contract' };

export default function TcTemplatesPage() {
    const api = useApiClient();
    const user = getCurrentUser();
    const canEdit = !!(user?.isManagingBroker || user?.staffRole === 'Office Manager');
    const [list, setList] = useState(null);
    const [state, setState] = useState('IL');
    const [pick, setPick] = useState({ side: 'buyer', deal_type: 'residential' });
    const [tpl, setTpl] = useState(null);
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');
    const [tcUser, setTcUser] = useState('');

    const loadList = useCallback(async () => {
        try {
            const r = await api.get('/api/tc/templates');
            setList(r); setTcUser(r.default_tc_user_id || '');
            setState((s) => (s === 'IL' && r.state === 'FL' ? 'FL' : s));
        } catch (e) { setErr(e.message); }
    }, [api]);
    useEffect(() => { loadList(); }, [loadList]);

    const path = `/api/tc/templates/${state}/${pick.side}/${pick.deal_type}`;
    const loadTpl = useCallback(async () => {
        try { setTpl(await api.get(path)); setErr(''); } catch (e) { setErr(e.message); }
    }, [api, path]);
    useEffect(() => { loadTpl(); }, [loadTpl]);

    if (!list) return <div className="p-6">{err ? <p className="text-red-700">{err}</p> : 'Loading templates…'}</div>;
    const ch = list.choices;
    const milestones = (ch.milestone_keys || {})[state] || [];
    const sideLabels = (ch.side_labels || {})[pick.deal_type] || {};

    const setStep = (i, k, val) => setTpl({ ...tpl, steps: tpl.steps.map((s, j) => (j === i ? { ...s, [k]: val } : s)) });
    const move = (i, d) => {
        const steps = [...tpl.steps]; const j = i + d;
        if (j < 0 || j >= steps.length) return;
        [steps[i], steps[j]] = [steps[j], steps[i]];
        setTpl({ ...tpl, steps });
    };
    const removeStep = (i) => setTpl({ ...tpl, steps: tpl.steps.filter((_, j) => j !== i) });
    const addStep = () => setTpl({ ...tpl, steps: [...tpl.steps, { ...BLANK }] });
    const flash = (m) => { setMsg(m); setErr(''); setTimeout(() => setMsg(''), 4000); };

    const save = async () => {
        try {
            const r = await api.put(path, { name: tpl.name, steps: tpl.steps.map((s) => ({ ...s, days: Number(s.days) || 0 })) });
            setTpl(r); flash('Template saved. New checklists use it; deals already running keep their own.'); loadList();
        } catch (e) { setErr(e.message); }
    };
    const reset = async () => {
        if (!window.confirm('Put this template back to the built-in standard steps? Your changes to it are discarded.')) return;
        try { setTpl(await api.delete(path)); flash('Template reset to the standard.'); loadList(); } catch (e) { setErr(e.message); }
    };
    const saveTcUser = async () => {
        try { await api.put('/api/tc/settings', { default_tc_user_id: tcUser ? Number(tcUser) : null }); flash('Default TC saved.'); } catch (e) { setErr(e.message); }
    };

    const row = (t) => list.templates.find((x) => x.state === state && x.side === t.side && x.deal_type === t.deal_type);

    return (
        <div className="p-6 space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-slate-800">TC checklist templates</h1>
                <p className="text-sm text-slate-500">The transaction coordinator's steps from contract to closing. A deal's checklist is created from one of these on the deal page; each deal can then change its own. Items marked [verify] depend on the contract or the municipality. <Link to="/dashboard/my-tc-items" className="text-indigo-700">My TC items</Link></p>
            </div>
            {err && <p className="text-red-700 text-sm">{err}</p>}
            {msg && <p className="text-emerald-700 text-sm">{msg}</p>}

            <section className="bg-white p-6 rounded-lg shadow-md space-y-3">
                <h2 className="text-lg font-semibold">Default transaction coordinator</h2>
                <p className="text-sm text-slate-500">Steps for "Office staff / TC" go to this person when a checklist is created (anyone can be chosen per step afterwards).</p>
                <div className="flex flex-wrap gap-2 items-end text-sm">
                    <select className={inp} value={tcUser} disabled={!canEdit} onChange={(e) => setTcUser(e.target.value)}>
                        <option value="">— nobody by default —</option>
                        {(list.office_people || []).map((p) => <option key={p.user_id} value={p.user_id}>{p.name} ({p.kind})</option>)}
                    </select>
                    {canEdit && <button onClick={saveTcUser} className="px-3 py-2 bg-slate-800 text-white rounded">Save</button>}
                </div>
            </section>

            <section className="bg-white p-6 rounded-lg shadow-md space-y-4">
                <div className="flex flex-wrap gap-3 items-end text-sm">
                    <label>State<select className={`block ${inp}`} value={state} onChange={(e) => setState(e.target.value)}><option value="IL">Illinois</option><option value="FL">Florida</option></select></label>
                    <label>Deal type<select className={`block ${inp}`} value={pick.deal_type} onChange={(e) => setPick({ ...pick, deal_type: e.target.value })}>
                        {ch.deal_types.map((t) => <option key={t} value={t}>{ch.type_labels[t]}</option>)}</select></label>
                    <label>Our side<select className={`block ${inp}`} value={pick.side} onChange={(e) => setPick({ ...pick, side: e.target.value })}>
                        {ch.sides.map((s) => <option key={s} value={s}>{sideLabels[s] || s}</option>)}</select></label>
                    {row(pick) && <span className="text-slate-500">{row(pick).is_default ? 'Built-in standard' : 'Edited by the office'} · {row(pick).step_count} steps</span>}
                </div>

                {tpl && (
                    <>
                        <label className="block text-sm">Name<input className={`block w-full ${inp}`} value={tpl.name || ''} disabled={!canEdit} onChange={(e) => setTpl({ ...tpl, name: e.target.value })} /></label>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead><tr className="text-left text-slate-500 border-b"><th className="py-2 pr-2">#</th><th className="pr-2">Step</th><th className="pr-2">Held by</th><th className="pr-2">Days</th><th className="pr-2">Day type</th><th className="pr-2">Counts from</th><th /></tr></thead>
                                <tbody>
                                    {tpl.steps.map((s, i) => (
                                        <tr key={`${s.skey || 'new'}-${i}`} className="border-b last:border-0 align-top">
                                            <td className="py-1 pr-2 text-slate-400">{i + 1}</td>
                                            <td className="py-1 pr-2 min-w-[18rem]"><textarea rows={2} className={`w-full ${inp}`} value={s.label} disabled={!canEdit} onChange={(e) => setStep(i, 'label', e.target.value)} /></td>
                                            <td className="py-1 pr-2"><select className={inp} value={s.role} disabled={!canEdit} onChange={(e) => setStep(i, 'role', e.target.value)}>
                                                {ch.roles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}</select></td>
                                            <td className="py-1 pr-2"><input type="number" className={`w-20 ${inp}`} value={s.days} disabled={!canEdit} onChange={(e) => setStep(i, 'days', e.target.value)} title="Negative = before" /></td>
                                            <td className="py-1 pr-2"><select className={inp} value={s.basis} disabled={!canEdit} onChange={(e) => setStep(i, 'basis', e.target.value)}>
                                                {Object.entries(BASIS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></td>
                                            <td className="py-1 pr-2"><select className={inp} value={s.anchor} disabled={!canEdit} onChange={(e) => setStep(i, 'anchor', e.target.value)}>
                                                <option value="contract">contract date</option><option value="closing">closing date</option>
                                                {milestones.filter((m) => m.mkey !== 'closing').map((m) => <option key={m.mkey} value={m.mkey}>{m.label}</option>)}
                                                {!['contract', 'closing'].includes(s.anchor) && !milestones.some((m) => m.mkey === s.anchor) && <option value={s.anchor}>{s.anchor} (not in the {state} milestones)</option>}
                                            </select></td>
                                            <td className="py-1 whitespace-nowrap space-x-1">
                                                {canEdit && <>
                                                    <button onClick={() => move(i, -1)} disabled={i === 0} className="px-1 text-slate-600 disabled:opacity-30" aria-label="Move up">↑</button>
                                                    <button onClick={() => move(i, 1)} disabled={i === tpl.steps.length - 1} className="px-1 text-slate-600 disabled:opacity-30" aria-label="Move down">↓</button>
                                                    <button onClick={() => removeStep(i)} className="px-1 text-red-700">Remove</button>
                                                </>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {canEdit && (
                            <div className="flex flex-wrap gap-2 text-sm">
                                <button onClick={addStep} className="px-3 py-2 bg-slate-200 rounded">Add a step</button>
                                <button onClick={save} className="px-3 py-2 bg-slate-800 text-white rounded">Save template</button>
                                {!tpl.is_default && <button onClick={reset} className="px-3 py-2 bg-slate-200 rounded">Reset to the standard</button>}
                            </div>
                        )}
                        {!canEdit && <p className="text-sm text-slate-500">The managing broker or the office manager edits these templates.</p>}
                    </>
                )}
            </section>
        </div>
    );
}
